import {
    IsoDateTimeSchema,
    ProfessionalApplicationPublicSchema,
    ProfessionalApplicationStatusSchema,
    UserRoleSchema,
    UuidSchema
} from '@cookspace-shared-contracts/core';
import { z } from 'zod';

/**
 * Локальные Zod-схемы для admin-эндпоинтов /professional-applications/*.
 * Согласно правилам shared-contracts, admin-only DTO живут локально
 * в admin-backend и не выносятся в shared-contracts; admin SPA дублирует
 * их форму здесь для строгого парсинга ответов через safeParse.
 *
 * Историческое название (до v0.10 / EPIC 1 фичи "Сотрудник + Кафе"):
 * chefApplicationsSchemas.
 */

export const SignedDocumentSchema = z.object({
    url: z.string(),
    expiresAt: IsoDateTimeSchema
});

export type SignedDocument = z.infer<typeof SignedDocumentSchema>;

export const ProfessionalApplicationDocumentsSchema = z.object({
    passport: SignedDocumentSchema.nullable(),
    education: SignedDocumentSchema.nullable(),
    experience: SignedDocumentSchema.nullable(),
    additional: z.array(SignedDocumentSchema)
});

export type ProfessionalApplicationDocuments = z.infer<
    typeof ProfessionalApplicationDocumentsSchema
>;

/**
 * Минимальное admin-представление пользователя в заявке: id, имя
 * и расшифрованный PII (email/phone). Полная UserPublic не подходит —
 * у admin-backend другой формат (phoneNumber вместо phone) и не все поля.
 */
export const AdminUserSummarySchema = z.object({
    id: UuidSchema,
    email: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    displayName: z.string().nullable()
});

export type AdminUserSummary = z.infer<typeof AdminUserSummarySchema>;

export const AdminProfessionalApplicationListItemSchema =
    ProfessionalApplicationPublicSchema.extend({
        user: AdminUserSummarySchema.nullable()
    });

export type AdminProfessionalApplicationListItem = z.infer<
    typeof AdminProfessionalApplicationListItemSchema
>;

export const ListProfessionalApplicationsResponseSchema = z.object({
    items: z.array(AdminProfessionalApplicationListItemSchema),
    total: z.number().int(),
    limit: z.number().int(),
    offset: z.number().int()
});

export type ListProfessionalApplicationsResponse = z.infer<
    typeof ListProfessionalApplicationsResponseSchema
>;

export const GetProfessionalApplicationResponseSchema = z.object({
    application: ProfessionalApplicationPublicSchema,
    user: AdminUserSummarySchema.nullable(),
    documents: ProfessionalApplicationDocumentsSchema
});

export type GetProfessionalApplicationResponse = z.infer<
    typeof GetProfessionalApplicationResponseSchema
>;

export const ApproveProfessionalApplicationRequestSchema = z.object({
    role: z.enum(['chef', 'professional_chef']),
    /**
     * R1 — набор узлов дерева специализаций, одобренных админом. Минимум 1.
     */
    approvedSpecializationIds: z.array(UuidSchema).min(1).max(50),
    reason: z.string().max(1000).optional()
});

export type ApproveProfessionalApplicationRequest = z.infer<
    typeof ApproveProfessionalApplicationRequestSchema
>;

export const ApproveProfessionalApplicationResponseSchema = z.object({
    application: ProfessionalApplicationPublicSchema
});

export type ApproveProfessionalApplicationResponse = z.infer<
    typeof ApproveProfessionalApplicationResponseSchema
>;

export const RejectProfessionalApplicationRequestSchema = z.object({
    reason: z.string().min(10).max(1000)
});

export type RejectProfessionalApplicationRequest = z.infer<
    typeof RejectProfessionalApplicationRequestSchema
>;

export const RejectProfessionalApplicationResponseSchema = z.object({
    application: ProfessionalApplicationPublicSchema
});

export type RejectProfessionalApplicationResponse = z.infer<
    typeof RejectProfessionalApplicationResponseSchema
>;

export const ListProfessionalApplicationsFiltersSchema = z.object({
    status: ProfessionalApplicationStatusSchema.optional(),
    search: z.string().optional(),
    limit: z.number().int().min(1).max(100).optional(),
    offset: z.number().int().min(0).optional()
});

export type ListProfessionalApplicationsFilters = z.infer<
    typeof ListProfessionalApplicationsFiltersSchema
>;

export { ProfessionalApplicationStatusSchema, UserRoleSchema, UuidSchema };
